// materialBuildModel — the material BUILD stream, pure.
//
// The transient, identity-keyed progress fold that mirrors the streaming-deltas
// contract (IDD f8a743b0): the deltas are side-effect-track infra (record ≠
// replay), the durable truth is the final `material_created` marker. Nothing
// here fetches, asks the clock, or mutates — `foldBuild` is a total reducer;
// elapsed time arrives as a stamped `atMs` on each delta (no wall clock). This
// is @bilda/client's own presentation vocabulary (it imports zero @bilda/core), fed
// by conversationAdapter parsing the wire delta at the boundary.
//
// Promoted verbatim in shape from sketches/material-building; the locale-bound
// phase/status LABELS moved OUT to the component (i18n macros) — the model
// exports only locale-neutral primitives (`elapsedLabel` is numeric m:ss;
// `questionCounts` is a pure tally the component renders through Lingui).

export type {
    BuildGeneratingDelta,
    BuildPhase,
    BuildPlannedDelta,
    BuildSettledDelta,
    BuildState,
    BuildStatus,
    BuildStoppedDelta,
    LiveSlot,
    MaterialBuildDelta,
    MaterialBuildDeltaKind,
    PhaseChangedDelta,
    PlannedSlot,
    QualityBand,
    QualityMeasure,
    QualityMeasureDetail,
    QualityMeasureStatus,
    QualityVerdict,
    SlotFilledDelta,
    SlotKind,
    SlotStartedDelta,
    SlotStatus,
};
export {
    elapsedLabel,
    foldBuild,
    initialBuild,
    isSlow,
    questionCounts,
    SLOW_THRESHOLD_MS,
};

// A slot in the material's outline. Module slots are structural anchors known at
// plan time; question slots stream their text in. `kind` is a runtime
// discriminant (genuinely different rows, different render), not a flag.
type SlotKind = "module" | "question";

// What `build_planned` hands over: the outline's SHAPE. Modules carry their
// heading; questions are placeholders whose text arrives later.
type PlannedSlot = {
    readonly id: string;
    readonly kind: SlotKind;
    // Present for modules (the heading, known at plan). Absent for questions —
    // their text streams in via `slot_filled`.
    readonly heading?: string;
};

// A slot's live status. `pending` → a skeleton row; `filling` → the soft agent
// shimmer; `filled` → real text with the "just changed" tint.
type SlotStatus = "pending" | "filling" | "filled";

// The folded live row.
type LiveSlot = {
    readonly id: string;
    readonly kind: SlotKind;
    readonly status: SlotStatus;
    // The row's text once known (a module's heading is known from plan; a
    // question's text arrives on `slot_filled`).
    readonly text: string | undefined;
};

// The honest sub-status — the tool's REAL current phase, not a canned carousel.
type BuildPhase = "planning" | "drafting" | "grounding" | "finalizing";

// === The delta sum — each carries identity (buildId + seq [+ slotId]) so the
// fold is find-or-create + idempotent: interleave fenced by buildId, smear +
// double-delivery fenced by seq, slot re-send overwrites its own row. ==========

// The outline arrives — gives the skeleton its shape (row count + structure).
type BuildPlannedDelta = {
    readonly type: "build_planned";
    readonly buildId: string;
    readonly seq: number;
    readonly atMs: number;
    readonly title: string;
    readonly slots: readonly PlannedSlot[];
};

// A slot goes pending → filling.
type SlotStartedDelta = {
    readonly type: "slot_started";
    readonly buildId: string;
    readonly seq: number;
    readonly atMs: number;
    readonly slotId: string;
};

// A slot goes filling → filled, carrying its text.
type SlotFilledDelta = {
    readonly type: "slot_filled";
    readonly buildId: string;
    readonly seq: number;
    readonly atMs: number;
    readonly slotId: string;
    readonly text: string;
};

// The honest sub-status advances.
type PhaseChangedDelta = {
    readonly type: "phase_changed";
    readonly buildId: string;
    readonly seq: number;
    readonly atMs: number;
    readonly phase: BuildPhase;
};

// === Quality verdict — the transient echo of the tolerant Quality→Chat seam
// Verdict (`App.Quality.Verdict`, packages/app/src/quality/verdict.ts, carried
// on `context.ts`'s `BuildEvaluatedDelta`). Mirrored STRUCTURALLY, never
// imported (@bilda/client imports zero @bilda/app/@bilda/core) — the seam
// contract is exactly this: `overall` to rank, `measures` to list OPAQUELY,
// so Quality can grow a measure with zero chat-side change. MaterialCard
// today reads `overall` alone (a single band badge); `measures` rides along
// for that same future-proofing, at no extra wire/model cost. ==============

type QualityBand = "blocked" | "weak" | "adequate" | "strong";

type QualityMeasureStatus =
    | { readonly type: "computed" }
    | { readonly type: "skipped"; readonly reason: string }
    | { readonly type: "failed"; readonly reason: string };

type QualityMeasureDetail = {
    readonly value: number;
    readonly unit?: string;
    readonly target?: number;
};

type QualityMeasure = {
    readonly key: string;
    readonly labelKey: string;
    readonly status: QualityMeasureStatus;
    readonly band?: QualityBand;
    readonly detail?: QualityMeasureDetail;
};

type QualityVerdict = {
    readonly overall: QualityBand;
    readonly measures: readonly QualityMeasure[];
};

// The transient echo of the quality VERDICT the tool computed for the
// in-progress material — emitted between `build_planned` and `build_settled`,
// only when the tool produced one (see context.ts's `mkBuildEvaluatedDelta`
// doc). A verdictless build simply never emits this; the fold's `verdict`
// field then stays `undefined` throughout.
type BuildEvaluatedDelta = {
    readonly type: "build_evaluated";
    readonly buildId: string;
    readonly seq: number;
    readonly atMs: number;
    readonly verdict: QualityVerdict;
};

// The transient echo of the durable `material_created` marker — lets the live
// card settle without a round-trip. The marker is the truth; this just flips
// the fold.
type BuildSettledDelta = {
    readonly type: "build_settled";
    readonly buildId: string;
    readonly seq: number;
    readonly atMs: number;
    readonly materialId: string;
    readonly title: string;
};

// The author's Stop landed — the partial is preserved.
type BuildStoppedDelta = {
    readonly type: "build_stopped";
    readonly buildId: string;
    readonly seq: number;
    readonly atMs: number;
};

// The generation-LIVENESS heartbeat (G3-interim-A): while the tool's input JSON
// streams, this fires repeatedly so the card keeps moving (elapsed advances,
// phase reads "Drafting…"). Fence-EXEMPT — it does NOT advance `appliedSeq`, so
// a flood of pulses never fences out the later terminal deltas. `chars` is a
// cosmetic running tick (never parsed); `seq` is carried for wire uniformity.
type BuildGeneratingDelta = {
    readonly type: "build_generating";
    readonly buildId: string;
    readonly seq: number;
    readonly atMs: number;
    readonly chars: number;
};

type MaterialBuildDelta =
    | BuildPlannedDelta
    | SlotStartedDelta
    | SlotFilledDelta
    | PhaseChangedDelta
    | BuildEvaluatedDelta
    | BuildSettledDelta
    | BuildStoppedDelta
    | BuildGeneratingDelta;

// The kind sum is derived from the union, never hand-maintained.
type MaterialBuildDeltaKind = MaterialBuildDelta["type"];

// === The folded live state ===================================================

type BuildStatus = "planning" | "building" | "settled" | "stopped";

type BuildState = {
    readonly buildId: string;
    readonly status: BuildStatus;
    // Known once `build_planned` (or `build_settled`) arrives.
    readonly title: string | undefined;
    readonly slots: readonly LiveSlot[];
    readonly phase: BuildPhase;
    // The highest seq applied — the idempotency + ordering guard.
    readonly appliedSeq: number;
    // The relative time of the last applied delta — the elapsed read, stamped,
    // never fetched from a clock.
    readonly elapsedMs: number;
    // Present once settled — the durable material's id (the affordance target).
    readonly materialId: string | undefined;
    // Present once the tool's quality verdict has echoed in (`build_evaluated`,
    // between planned and settled). Undefined for a verdictless build, or
    // before the evaluation delta has arrived.
    readonly verdict: QualityVerdict | undefined;
};

const initialBuild = (buildId: string): BuildState => ({
    buildId,
    status: "planning",
    title: undefined,
    slots: [],
    phase: "planning",
    appliedSeq: 0,
    elapsedMs: 0,
    materialId: undefined,
    verdict: undefined,
});

const plannedToLive = (slot: PlannedSlot): LiveSlot => ({
    id: slot.id,
    kind: slot.kind,
    // A module's heading is known at plan → it renders filled immediately as a
    // structural anchor. A question is pending until its text streams in.
    status: slot.kind === "module" ? "filled" : "pending",
    text: slot.heading,
});

const withSlot = (
    slots: readonly LiveSlot[],
    slotId: string,
    update: (slot: LiveSlot) => LiveSlot,
): readonly LiveSlot[] =>
    slots.map((slot) => (slot.id === slotId ? update(slot) : slot));

// The pure reducer. Fences, in order:
//   1. buildId mismatch → ignore (interleave fence).
//   2. seq ≤ appliedSeq → ignore (smear + double-delivery fence).
// Then a total match over the delta sum. A slot update is find-or-create by
// slotId (here: find; the plan seeds every slot), so a re-send overwrites its
// own row and never appends.
const foldBuild = (
    state: BuildState,
    delta: MaterialBuildDelta,
): BuildState => {
    if (delta.buildId !== state.buildId) return state;
    // G3-interim-A — the liveness heartbeat is fence-EXEMPT: it advances the
    // visible elapsed + phase but NOT `appliedSeq`, so the terminal deltas
    // (whose own low seqs are stamped at coda time) are never fenced out by the
    // pulses that arrived before them. Handled before the seq guard; it never
    // touches `status`, so a not-yet-planned build keeps its skeleton placeholder
    // while the phase reads "Drafting…". Idempotent under double-delivery.
    if (delta.type === "build_generating") {
        return { ...state, phase: "drafting", elapsedMs: delta.atMs };
    }
    if (delta.seq <= state.appliedSeq) return state;

    const base = { ...state, appliedSeq: delta.seq, elapsedMs: delta.atMs };

    switch (delta.type) {
        case "build_planned":
            return {
                ...base,
                status: "building",
                title: delta.title,
                slots: delta.slots.map(plannedToLive),
                phase: "planning",
            };
        case "slot_started":
            return {
                ...base,
                slots: withSlot(state.slots, delta.slotId, (slot) => ({
                    ...slot,
                    status: "filling",
                })),
            };
        case "slot_filled":
            return {
                ...base,
                slots: withSlot(state.slots, delta.slotId, (slot) => ({
                    ...slot,
                    status: "filled",
                    text: delta.text,
                })),
            };
        case "phase_changed":
            return { ...base, phase: delta.phase };
        case "build_evaluated":
            return { ...base, verdict: delta.verdict };
        case "build_settled":
            return {
                ...base,
                status: "settled",
                title: delta.title,
                materialId: delta.materialId,
                phase: "finalizing",
            };
        case "build_stopped":
            return { ...base, status: "stopped" };
        default: {
            // Exhaustiveness: a new delta variant is `never` here.
            const exhaustive: never = delta;
            return exhaustive;
        }
    }
};

// === Locale-neutral read helpers (the component renders the labels) ==========

// The question-slot tally — the ONE number the honest sub-status needs
// ("question 3 of 8"). Pure; the component wraps it in an i18n phrase.
const questionCounts = (
    state: BuildState,
): { readonly filled: number; readonly total: number } => {
    const questions = state.slots.filter((slot) => slot.kind === "question");
    return {
        filled: questions.filter((slot) => slot.status === "filled").length,
        total: questions.length,
    };
};

// Elapsed as "m:ss" — pure formatting of the stamped elapsedMs. Locale-neutral
// (digits + colon), so it needs no dictionary.
const elapsedLabel = (ms: number): string => {
    const totalSeconds = Math.max(0, Math.floor(ms / 1000));
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
};

// The soft threshold past which the wait gets a gentle "taking longer" hint —
// never an eternal spinner (alarm-discipline: one soft escalation, no alarm).
const SLOW_THRESHOLD_MS = 30_000;

const isSlow = (ms: number): boolean => ms >= SLOW_THRESHOLD_MS;
